import React, { useState } from 'react';
import { DollarSign, Heart, X, ArrowLeftRight } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { apiService } from '../services/api';
import { useToast } from '../hooks/use-toast';

const TipResponseModal = ({ isOpen, onClose, tipData, onResponse }) => {
  const [selectedAction, setSelectedAction] = useState(null);
  const [counterAmount, setCounterAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleResponse = async (action) => {
    if (!tipData.swipe_id) {
      toast({
        title: "Error",
        description: "Invalid tip data. Please try again.",
        variant: "destructive"
      });
      return;
    }

    try {
      setLoading(true);
      
      const responseData = {
        swipe_id: tipData.swipe_id,
        action: action
      };

      if (action === 'counter') {
        const amount = parseFloat(counterAmount);
        if (!amount || amount < 1 || amount > 1000) {
          toast({
            title: "Invalid Amount",
            description: "Counter amount must be between $1 and $1000.",
            variant: "destructive"
          });
          return;
        }
        responseData.counter_amount = amount;
      }

      const response = await apiService.tips.respondToTip(responseData);
      
      if (response.data.success) {
        const actionMessages = {
          accept: "Tip accepted! 💰 It's a match!",
          reject: "Tip declined. 👋",
          counter: `Counter offer sent! 🔄 ($${counterAmount})`
        };
        
        toast({
          title: "Response Sent!",
          description: actionMessages[action]
        });
        
        onResponse?.();
        onClose();
      }
    } catch (error) {
      console.error('Error responding to tip:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to respond to tip.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedAction(null);
    setCounterAmount('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!tipData) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <DollarSign className="w-5 h-5 text-green-500" />
            <span>Tip Offer Response</span>
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Tip Details */}
          <Card className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
            <div className="text-center space-y-2">
              <div className="flex items-center justify-center space-x-2">
                <DollarSign className="w-6 h-6 text-green-600" />
                <span className="text-2xl font-bold text-green-700">
                  ${tipData.amount}
                </span>
              </div>
              <p className="text-sm text-gray-700">
                <strong>{tipData.from_user_name}</strong> sent you a tip offer
              </p>
              {tipData.message && (
                <div className="bg-white/60 rounded-lg p-2 mt-2">
                  <p className="text-xs text-gray-600 italic">
                    "{tipData.message}"
                  </p>
                </div>
              )}
            </div>
          </Card>

          {!selectedAction ? (
            /* Action Selection */
            <div className="space-y-3">
              <p className="text-sm text-gray-600 text-center">
                How would you like to respond?
              </p>
              
              <div className="grid grid-cols-1 gap-2">
                {/* Accept Button */}
                <Button
                  variant="outline"
                  className="flex items-center justify-between p-4 h-auto border-green-200 hover:bg-green-50"
                  onClick={() => setSelectedAction('accept')}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <Heart className="w-4 h-4 text-green-600" />
                    </div>
                    <div className="text-left">
                      <div className="font-medium text-green-700">Accept & Match</div>
                      <div className="text-xs text-gray-500">Accept the tip and create a match</div>
                    </div>
                  </div>
                  <Badge className="bg-green-500 text-white">Recommended</Badge>
                </Button>

                {/* Counter Button */}
                <Button
                  variant="outline"
                  className="flex items-center justify-between p-4 h-auto border-blue-200 hover:bg-blue-50"
                  onClick={() => setSelectedAction('counter')}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <ArrowLeftRight className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="text-left">
                      <div className="font-medium text-blue-700">Counter Offer</div>
                      <div className="text-xs text-gray-500">Suggest a different amount</div>
                    </div>
                  </div>
                </Button>

                {/* Decline Button */}
                <Button
                  variant="outline"
                  className="flex items-center justify-between p-4 h-auto border-red-200 hover:bg-red-50"
                  onClick={() => setSelectedAction('reject')}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                      <X className="w-4 h-4 text-red-600" />
                    </div>
                    <div className="text-left">
                      <div className="font-medium text-red-700">Decline</div>
                      <div className="text-xs text-gray-500">Politely decline the tip offer</div>
                    </div>
                  </div>
                </Button>
              </div>
            </div>
          ) : (
            /* Confirmation */
            <div className="space-y-4">
              {selectedAction === 'accept' && (
                <div className="text-center space-y-2">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                    <Heart className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="font-semibold text-green-700">Accept & Match</h3>
                  <p className="text-sm text-gray-600">
                    You'll accept the ${tipData.amount} tip and create a match with {tipData.from_user_name}.
                  </p>
                </div>
              )}

              {selectedAction === 'counter' && (
                <div className="space-y-3">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                      <ArrowLeftRight className="w-8 h-8 text-blue-600" />
                    </div>
                    <h3 className="font-semibold text-blue-700 mt-2">Counter Offer</h3>
                    <p className="text-sm text-gray-600">
                      Suggest a different tip amount to {tipData.from_user_name}.
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="counter-amount">Your counter offer ($1 - $1000)</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="counter-amount"
                        type="number"
                        min="1"
                        max="1000"
                        step="1"
                        placeholder="50"
                        value={counterAmount}
                        onChange={(e) => setCounterAmount(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                  </div>
                </div>
              )}

              {selectedAction === 'reject' && (
                <div className="text-center space-y-2">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                    <X className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="font-semibold text-red-700">Decline Tip</h3>
                  <p className="text-sm text-gray-600">
                    You'll politely decline the tip offer from {tipData.from_user_name}.
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setSelectedAction(null)}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button
                  onClick={() => handleResponse(selectedAction)}
                  disabled={loading || (selectedAction === 'counter' && !counterAmount)}
                  className={`flex-1 ${
                    selectedAction === 'accept' ? 'bg-green-500 hover:bg-green-600' :
                    selectedAction === 'counter' ? 'bg-blue-500 hover:bg-blue-600' :
                    'bg-red-500 hover:bg-red-600'
                  }`}
                >
                  {loading ? 'Sending...' : `Confirm ${selectedAction === 'reject' ? 'Decline' : selectedAction === 'counter' ? 'Counter' : 'Accept'}`}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TipResponseModal;